// Chat interno (D71): boton en la barra superior que abre un panel con el canal General, los mensajes directos (con la
// presencia de cada persona en vivo) y los hilos de casos y clientes. Desde un caso, "Hablar con el equipo" abre su hilo.
// @Nombre menciona a alguien: le llega un aviso aunque tenga el chat cerrado.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';

import { canalDirecto, enviarMensaje, mencionesEn, mensajesDe, recientes } from '../api/chat';
import { useSession } from '../auth/session';
import { useMiembros } from '../data/queries';
import { ago } from '../lib/format';
import { Icon } from '../lib/icons';
import { alRecibir, avisarEscribiendo, avisarMensaje, type MensajeChat, type Presente, useTiempoReal } from '../lib/tiempoReal';
import { toast } from './toasts';

// Abrir el chat desde cualquier pantalla (p. ej. el hilo de un caso).
type Peticion = { canal: string; titulo?: string } | null;
let peticion: Peticion = null;
const subs = new Set<() => void>();
export function abrirChat(canal = 'general', titulo?: string) {
  peticion = { canal, titulo };
  subs.forEach((f) => f());
}
const usePeticion = () =>
  useSyncExternalStore(
    (f) => {
      subs.add(f);
      return () => {
        subs.delete(f);
      };
    },
    () => peticion,
  );

// Ultima vez que esta persona vio cada canal (en este navegador).
const VISTO = 'consola.chat.visto';
const leerVisto = (): Record<string, string> => {
  try {
    return JSON.parse(localStorage.getItem(VISTO) ?? '{}');
  } catch {
    return {};
  }
};
const marcarVisto = (canal: string) => {
  try {
    localStorage.setItem(VISTO, JSON.stringify({ ...leerVisto(), [canal]: new Date().toISOString() }));
  } catch {
    /* sin almacenamiento */
  }
};

const ESTADO: Record<string, [string, string]> = {
  disponible: ['Disponible', 'var(--ok)'],
  'en-llamada': ['En llamada', 'var(--warn)'],
  ocupado: ['Ocupado', 'var(--warn)'],
  dnd: ['No molestar', 'var(--bad)'],
  pausa: ['En pausa', 'var(--faint)'],
  ausente: ['Ausente', 'var(--faint)'],
};

function Punto({ p }: { p?: Presente }) {
  const [txt, color] = p ? (ESTADO[p.estado] ?? ESTADO.disponible) : ['Desconectado', 'transparent'];
  return <span title={txt} aria-label={txt} style={{ width: 8, height: 8, borderRadius: 4, flex: 'none', background: color, boxShadow: p ? 'none' : 'inset 0 0 0 1.5px var(--faint)' }} />;
}

export function Chat() {
  const { yo, session } = useSession();
  const miembroId = (yo?.miembroId ?? '').toLowerCase();
  const rt = useTiempoReal();
  const qc = useQueryClient();
  const miembros = useMiembros();
  const pedido = usePeticion();
  const [abierto, setAbierto] = useState(false);
  const [canal, setCanal] = useState('general');
  const [titulo, setTitulo] = useState<string | undefined>();
  const [texto, setTexto] = useState('');
  const [escribe, setEscribe] = useState<Record<string, { nombre: string; hasta: number }>>({});
  const desde = useMemo(() => new Date(Date.now() - 14 * 864e5).toISOString(), []);
  const ultimos = useQuery({ queryKey: ['chat-recientes', miembroId], queryFn: () => recientes(miembroId, desde), enabled: !!miembroId && session.modo === 'usuario', refetchInterval: 120_000 });
  const conv = useQuery({ queryKey: ['chat', canal], queryFn: () => mensajesDe(canal), enabled: abierto && !!miembroId });
  const fin = useRef<HTMLDivElement>(null);
  const [, forzar] = useState(0);

  useEffect(() => {
    if (!pedido) return;
    setCanal(pedido.canal);
    setTitulo(pedido.titulo);
    setAbierto(true);
  }, [pedido]);

  // Mensajes nuevos al instante: se agregan a la conversacion y a la lista; si mencionan a esta persona, aviso.
  useEffect(
    () =>
      alRecibir((e) => {
        if (e.tipo === 'chat') {
          const m = (e as { mensaje: MensajeChat }).mensaje;
          qc.setQueryData<MensajeChat[]>(['chat', m.canal], (xs) => (xs && !xs.some((x) => x.id === m.id) ? [...xs, m] : xs));
          qc.setQueryData<MensajeChat[]>(['chat-recientes', miembroId], (xs) => (xs && !xs.some((x) => x.id === m.id) ? [m, ...xs] : xs));
          const paraMi = (m.menciones ?? []).map((x) => x.toLowerCase()).includes(miembroId) || (m.canal.startsWith('dm:') && m.autorId.toLowerCase() !== miembroId);
          if (paraMi && !(abierto && canal === m.canal)) {
            toast(`${m.autorNombre ?? 'Alguien'}: ${m.name.slice(0, 80)}`, 'info');
            if (document.hidden && 'Notification' in window && Notification.permission === 'granted') new Notification(`Mensaje de ${m.autorNombre ?? 'tu equipo'}`, { body: m.name.slice(0, 140), tag: `chat-${m.canal}` });
          }
          if (abierto && canal === m.canal) marcarVisto(canal);
          forzar((n) => n + 1);
        }
        if (e.tipo === 'escribiendo') {
          const x = e as { canal: string; nombre: string };
          setEscribe((v) => ({ ...v, [x.canal]: { nombre: x.nombre, hasta: Date.now() + 4000 } }));
        }
      }),
    [qc, miembroId, abierto, canal],
  );

  useEffect(() => {
    if (!abierto) return;
    marcarVisto(canal);
    fin.current?.scrollIntoView({ block: 'end' });
  }, [abierto, canal, conv.data?.length]);

  if (session.modo !== 'usuario' || !miembroId) return null;

  const visto = leerVisto();
  const lista = ultimos.data ?? [];
  const sinLeer = (c: string) => lista.filter((m) => m.canal === c && m.autorId.toLowerCase() !== miembroId && (!visto[c] || m.createdAt > visto[c])).length;
  const totalSinLeer = [...new Set(lista.map((m) => m.canal))].reduce((n, c) => n + sinLeer(c), 0);
  const presenciaDe = new Map(rt.presencia.map((p) => [p.miembroId.toLowerCase(), p]));
  const otros = (miembros.data ?? []).filter((m) => m.id.toLowerCase() !== miembroId);
  const hilos = [...new Map(lista.filter((m) => !m.canal.startsWith('dm:') && m.canal !== 'general').map((m) => [m.canal, m.titulo ?? m.canal])).entries()].slice(0, 8);
  const nombreDe = (id: string) => (miembros.data ?? []).find((m) => m.id.toLowerCase() === id.toLowerCase())?.nombre ?? 'Alguien';
  const tituloCanal = canal === 'general' ? 'General' : canal.startsWith('dm:') ? nombreDe(canal.split(':').slice(1).find((x) => x !== miembroId) ?? '') : (titulo ?? lista.find((m) => m.canal === canal)?.titulo ?? 'Hilo');
  const quienEscribe = escribe[canal] && escribe[canal].hasta > Date.now() ? escribe[canal].nombre : null;

  const enviar = async () => {
    const t = texto.trim();
    if (!t) return;
    setTexto('');
    try {
      const m = await enviarMensaje({ canal, texto: t, autorId: miembroId, menciones: mencionesEn(t, miembros.data ?? []), titulo: canal.startsWith('dm:') || canal === 'general' ? undefined : tituloCanal });
      const completo = { ...m, autorNombre: yo?.nombre };
      qc.setQueryData<MensajeChat[]>(['chat', canal], (xs) => [...(xs ?? []), completo]);
      qc.setQueryData<MensajeChat[]>(['chat-recientes', miembroId], (xs) => [completo, ...(xs ?? [])]);
      avisarMensaje(m.id);
    } catch (e) {
      setTexto(t);
      toast(`No se envió: ${(e as Error).message}`, 'error');
    }
  };

  const ir = (c: string, tt?: string) => {
    setCanal(c);
    setTitulo(tt);
  };

  return (
    <>
      <button className="btn ghost chat-btn" type="button" onClick={() => setAbierto((x) => !x)} aria-label={`Chat del equipo${totalSinLeer ? `, ${totalSinLeer} sin leer` : ''}`} aria-expanded={abierto} style={{ position: 'relative' }}>
        <Icon name="reply" />
        {totalSinLeer ? <span className="chat-badge">{totalSinLeer > 9 ? '9+' : totalSinLeer}</span> : null}
      </button>
      {abierto
        ? createPortal(
            <aside className="chat-panel" role="dialog" aria-label="Chat del equipo">
              <nav className="chat-canales" aria-label="Canales">
                <div className="chat-h">
                  <b>Equipo</b>
                  <span className="sub" title={rt.conectado ? 'Conectado en vivo' : 'Sin conexión en vivo: se actualiza cada 2 minutos'}>
                    {rt.conectado ? 'En vivo' : 'Reconectando…'}
                  </span>
                </div>
                <button type="button" className={`chat-canal ${canal === 'general' ? 'on' : ''}`} onClick={() => ir('general')}>
                  <Icon name="users" /> General {sinLeer('general') ? <span className="chat-n">{sinLeer('general')}</span> : null}
                </button>
                <span className="lbl" style={{ margin: '10px 8px 4px' }}>
                  Personas
                </span>
                {otros.map((m) => {
                  const c = canalDirecto(miembroId, m.id);
                  const p = presenciaDe.get(m.id.toLowerCase());
                  return (
                    <button key={m.id} type="button" className={`chat-canal ${canal === c ? 'on' : ''}`} onClick={() => ir(c)} title={p ? `${ESTADO[p.estado]?.[0] ?? ''}${p.detalle ? ` · ${p.detalle}` : ''}` : 'Desconectado'}>
                      <Punto p={p} /> {m.nombre} {sinLeer(c) ? <span className="chat-n">{sinLeer(c)}</span> : null}
                    </button>
                  );
                })}
                {hilos.length ? (
                  <span className="lbl" style={{ margin: '10px 8px 4px' }}>
                    Hilos
                  </span>
                ) : null}
                {hilos.map(([c, t]) => (
                  <button key={c} type="button" className={`chat-canal ${canal === c ? 'on' : ''}`} onClick={() => ir(c, t)}>
                    <Icon name="tag" /> <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t}</span> {sinLeer(c) ? <span className="chat-n">{sinLeer(c)}</span> : null}
                  </button>
                ))}
              </nav>
              <section className="chat-conv">
                <header className="chat-h">
                  <b>{tituloCanal}</b>
                  <button className="btn sm ghost" type="button" aria-label="Cerrar chat" onClick={() => setAbierto(false)}>
                    <Icon name="x" />
                  </button>
                </header>
                <div className="chat-msgs">
                  {conv.isLoading ? <p className="note">Cargando…</p> : null}
                  {conv.data && !conv.data.length ? <p className="note">Aún no hay mensajes. Escribe el primero{canal === 'general' ? ' para todo el equipo' : ''}.</p> : null}
                  {(conv.data ?? []).map((m) => {
                    const mio = m.autorId.toLowerCase() === miembroId;
                    return (
                      <div key={m.id} className={`chat-msg ${mio ? 'mio' : ''}`}>
                        {!mio ? <span className="chat-autor">{m.autorNombre ?? nombreDe(m.autorId)}</span> : null}
                        <p>{m.name}</p>
                        <span className="chat-hora">{ago(m.createdAt)}</span>
                      </div>
                    );
                  })}
                  <div ref={fin} />
                </div>
                <p className="chat-escribe" aria-live="polite">
                  {quienEscribe ? `${quienEscribe} está escribiendo…` : ''}
                </p>
                <form
                  className="chat-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void enviar();
                  }}
                >
                  <label htmlFor="chat-texto" className="sr-only">
                    Mensaje
                  </label>
                  <textarea
                    id="chat-texto"
                    rows={2}
                    value={texto}
                    placeholder={`Escribe a ${tituloCanal}… (@nombre para avisar a alguien)`}
                    onChange={(e) => {
                      setTexto(e.target.value);
                      avisarEscribiendo(canal);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        void enviar();
                      }
                    }}
                  />
                  <button className="btn primary" type="submit" disabled={!texto.trim()}>
                    <Icon name="send" />
                  </button>
                </form>
              </section>
            </aside>,
            document.body,
          )
        : null}
    </>
  );
}
